import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import emailjs from 'emailjs-com';
import Footer from '../components/Footer';
import { BackgroundBeams } from '../components/BackgroundBeams';
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaClock,
  FaTimes,
  FaWhatsapp,
  FaLinkedinIn,
  FaInstagram,
  FaCheckCircle,
  FaExclamationCircle
} from 'react-icons/fa';
import '../pages/ContactPage.css';

const ContactUs = () => {
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
    newsletter: false
  });

  const [formStatus, setFormStatus] = useState({
    submitting: false,
    submitted: false,
    error: null
  });

  const contactInfo = [
    {
      icon: <FaMapMarkerAlt />,
      title: 'Visit Us',
      content: 'Office no 2, Farmer CHS Plot No.32, Sector No.12, Nr. Honda Service Centre, Kharghar, Navi Mumbai.',
      link: 'https://maps.app.goo.gl/XHWj98ZjP6ffPUM58'
    },
    {
      icon: <FaPhone />,
      title: 'Call Us',
      content: (
        <>
          <a href="tel:+917600236927" style={{ display: 'block' }}>+91 7600236927</a>
          <a href="tel:+916350462627" style={{ display: 'block', marginTop: '4px' }}>+91 6350462627</a>
        </>
      ),
      link: null
    },
    {
      icon: <FaEnvelope />,
      title: 'Email Us',
      content: 'info.deployx@gmail.com',
      link: 'mailto:info.deployx@gmail.com'
    },
    {
      icon: <FaClock />,
      title: 'Working Hours',
      content: 'Mon - Fri: 9:00 AM - 5:00 PM',
      link: null
    }
  ];

  const socialLinks = [
    { icon: <FaWhatsapp />, url: 'https://wa.me/+917600236927', label: 'WhatsApp' },
    { icon: <FaLinkedinIn />, url: '#', label: 'LinkedIn' },
    { icon: <FaInstagram />, url: 'https://www.instagram.com/deploy.x_?igsh=MXVobHRjMDdtbXMzbg==', label: 'Instagram' }
  ];

  const reasons = [
    {
      number: '01',
      title: 'Expert Team',
      description: 'Work with experienced professionals who understand your needs'
    },
    {
      number: '02',
      title: 'Quick Response',
      description: 'Get responses within 24 hours on business days'
    },
    {
      number: '03',
      title: 'Tailored Solutions',
      description: 'Receive customized solutions designed for your business'
    }
  ];

  const [showWhatsAppTooltip, setShowWhatsAppTooltip] = useState(false);

  // WhatsApp configuration
  const whatsappNumber = '+917600236927';
  const whatsappMessage = 'Hi! I would like to discuss a project with DEPLOY-X.';
  const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ submitting: true, submitted: false, error: null });

    // EmailJS configuration
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID';
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID';
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';

    // Template parameters - using multiple naming conventions for better EmailJS compatibility
    const templateParams = {
      // Name variations
      from_name: `${formData.firstName} ${formData.lastName}`,
      user_name: `${formData.firstName} ${formData.lastName}`,
      name: `${formData.firstName} ${formData.lastName}`,
      first_name: formData.firstName,
      last_name: formData.lastName,

      // Email variations
      from_email: formData.email,
      user_email: formData.email,
      email: formData.email,
      reply_to: formData.email,

      // Other fields
      phone: formData.phone || 'Not provided',
      phone_number: formData.phone || 'Not provided',
      company: formData.company || 'Not provided',
      company_name: formData.company || 'Not provided',
      service: formData.service,
      service_type: formData.service,
      message: formData.message,
      user_message: formData.message,
      newsletter: formData.newsletter ? 'Yes' : 'No',
      to_email: 'info.deployx@gmail.com'
    };

    // Debug log
    console.log('Sending email with params:', templateParams);

    try {
      await emailjs.send(
        serviceId,
        templateId,
        templateParams,
        publicKey
      );

      setFormStatus({ submitting: false, submitted: true, error: null });

      // Reset form after successful submission
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: '',
        newsletter: false
      });

      // Hide success message after 5 seconds
      setTimeout(() => {
        setFormStatus({ submitting: false, submitted: false, error: null });
      }, 5000);

    } catch (error) {
      console.error('EmailJS Error:', error);
      setFormStatus({
        submitting: false,
        submitted: false,
        error: 'Failed to send message. Please try again or contact us directly.'
      });
    }
  };

  // Auto-show tooltip after 3 seconds
  React.useEffect(() => {
    const tooltipTimer = setTimeout(() => {
      setShowWhatsAppTooltip(true);
      // Auto-hide after 5 seconds
      setTimeout(() => setShowWhatsAppTooltip(false), 5000);
    }, 3000);
    return () => clearTimeout(tooltipTimer);
  }, []);

  return (
    <>
      <motion.div
        className="contact-us-page"
        initial={{ opacity: 0, scale: 0.95, filter: "blur(10px)" }}
        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
        transition={{
          duration: 1,
          ease: [0.25, 0.46, 0.45, 0.94],
          scale: {
            type: "spring",
            damping: 20,
            stiffness: 100
          },
          filter: {
            duration: 0.8
          }
        }}
      >

        <main>
          {/* Hero Section */}
          <section className="contact-hero">
            <BackgroundBeams />
            <div className="hero-overlay"></div>
            <div className="container">
              <motion.div
                className="contact-hero-content"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <motion.span
                  className="hero-badge"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  Get In Touch
                </motion.span>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                >
                  Let's Build Something <span className="gradient-text">Amazing Together</span>
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  Ready to transform your business with innovative solutions? Our team is here to help you achieve your goals.
                </motion.p>
              </motion.div>

              {/* Contact Info Cards */}
              <motion.div
                className="contact-info-grid"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
              >
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    className="info-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                    whileHover={{ y: -5, transition: { duration: 0.3 } }}
                  >
                    <div className="info-icon">{info.icon}</div>
                    <div className="info-content">
                      <h3>{info.title}</h3>
                      {info.link ? (
                        <a href={info.link} target="_blank" rel="noopener noreferrer">
                          {info.content}
                        </a>
                      ) : (
                        <p>{info.content}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* Main Contact Section with Form */}
          <section className="main-contact-section">
            <div className="container">
              <div className="contact-page-layout">
                {/* Left Side - Why Choose Us */}
                <motion.div
                  className="contact-sidebar"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <div className="sidebar-content">
                    <span className="section-label">Why Choose Us</span>
                    <h2>We're Here to Help You Succeed</h2>
                    <p>
                      Our dedicated team is committed to providing exceptional service and innovative solutions tailored to your unique needs.
                    </p>

                    <div className="reasons-list">
                      {reasons.map((reason, index) => (
                        <motion.div
                          key={index}
                          className="reason-item"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                          viewport={{ once: true }}
                        >
                          <div className="reason-number">{reason.number}</div>
                          <div className="reason-content">
                            <h4>{reason.title}</h4>
                            <p>{reason.description}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    {/* Social Links */}
                    <div className="social-section">
                      <h4>Follow Us</h4>
                      <div className="social-links">
                        {socialLinks.map((social, index) => (
                          <motion.a
                            key={index}
                            href={social.url}
                            className="social-link"
                            aria-label={social.label}
                            initial={{ opacity: 0, scale: 0 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3, delay: 0.5 + index * 0.05 }}
                            viewport={{ once: true }}
                            whileHover={{ scale: 1.1, rotate: 5 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            {social.icon}
                          </motion.a>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Right Side - Contact Form */}
                <motion.div
                  className="contact-form-wrapper"
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <div className="form-header">
                    <h3>Send Us a Message</h3>
                    <p>Fill out the form below and we'll get back to you within 24 hours</p>
                  </div>

                  {/* Success/Error Messages */}
                  <AnimatePresence>
                    {formStatus.submitted && (
                      <motion.div
                        className="form-message success"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        style={{
                          padding: '15px',
                          marginBottom: '20px',
                          borderRadius: '8px',
                          background: 'rgba(34, 197, 94, 0.1)',
                          border: '1px solid rgba(34, 197, 94, 0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          color: '#22c55e'
                        }}
                      >
                        <FaCheckCircle />
                        <span>Message sent successfully! We'll get back to you soon.</span>
                      </motion.div>
                    )}
                    {formStatus.error && (
                      <motion.div
                        className="form-message error"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        style={{
                          padding: '15px',
                          marginBottom: '20px',
                          borderRadius: '8px',
                          background: 'rgba(239, 68, 68, 0.1)',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          color: '#ef4444'
                        }}
                      >
                        <FaExclamationCircle />
                        <span>{formStatus.error}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <form className="contact-form" onSubmit={handleSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="firstName">First Name *</label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          placeholder="First Name"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          disabled={formStatus.submitting}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="lastName">Last Name *</label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          placeholder="Last Name"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          disabled={formStatus.submitting}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          placeholder="Type your email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          disabled={formStatus.submitting}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="phone">Phone Number</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          placeholder="Phone Number"
                          value={formData.phone}
                          onChange={handleInputChange}
                          disabled={formStatus.submitting}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="company">Company Name</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        placeholder="Your Company"
                        value={formData.company}
                        onChange={handleInputChange}
                        disabled={formStatus.submitting}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="service">Service Interested In *</label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        required
                        disabled={formStatus.submitting}
                      >
                        <option value="">Select a service</option>
                        <option value="ai">AI Solutions</option>
                        <option value="web">Web Development</option>
                        <option value="mobile">Mobile Apps</option>
                        <option value="business">Business Solutions</option>
                        <option value="nfc">NFC Cards</option>
                        <option value="cyber">Cyber Solutions</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="message">Tell Us About Your Project *</label>
                      <textarea
                        id="message"
                        name="message"
                        rows="5"
                        placeholder="Describe your project, goals, and requirements..."
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        disabled={formStatus.submitting}
                      ></textarea>
                    </div>

                    <div className="form-group checkbox-group">
                      <input
                        type="checkbox"
                        id="newsletter"
                        name="newsletter"
                        checked={formData.newsletter}
                        onChange={handleInputChange}
                        disabled={formStatus.submitting}
                      />
                      <label htmlFor="newsletter">
                        I'd like to receive updates and newsletters from DEPLOY-X
                      </label>
                    </div>

                    <motion.button
                      type="submit"
                      className="submit-button"
                      whileHover={{ scale: formStatus.submitting ? 1 : 1.02 }}
                      whileTap={{ scale: formStatus.submitting ? 1 : 0.98 }}
                      disabled={formStatus.submitting}
                      style={{
                        opacity: formStatus.submitting ? 0.7 : 1,
                        cursor: formStatus.submitting ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {formStatus.submitting ? 'Sending...' : 'Send Message'}
                    </motion.button>
                  </form>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Map Section */}
          <section className="map-section">
            <div className="container">
              <motion.div
                className="map-wrapper"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div style={{ width: '100%', height: '450px', borderRadius: '15px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                  <iframe
                    src="https://maps.google.com/maps?q=19.0468941,73.0652924&t=&z=17&ie=UTF8&iwloc=&output=embed"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="DEPLOY-X Location"
                  ></iframe>
                </div>
              </motion.div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="contact-cta">
            <div className="container">
              <motion.div
                className="cta-content"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2>Prefer to Talk Directly?</h2>
                <p>Schedule a call with our team to discuss your project in detail</p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <motion.a
                    href="tel:+917600236927"
                    className="cta-button-large"
                    whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(0, 191, 255, 0.3)" }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaPhone /> +91 7600236927
                  </motion.a>
                  <motion.a
                    href="tel:+916350462627"
                    className="cta-button-large"
                    whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(0, 191, 255, 0.3)" }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaPhone /> +91 6350462627
                  </motion.a>
                </div>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </motion.div>

      {/* WhatsApp Floating Button */}
      <div className="whatsapp-float-container">
        <AnimatePresence>
          {showWhatsAppTooltip && (
            <motion.div
              className="whatsapp-tooltip"
              initial={{ opacity: 0, x: 20, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 20, scale: 0.8 }}
              transition={{ duration: 0.3 }}
            >
              <button className="tooltip-close" onClick={() => setShowWhatsAppTooltip(false)}>
                <FaTimes />
              </button>
              <p className="tooltip-text">
                <strong>Need Help?</strong><br />
                Chat with us on WhatsApp!
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.a
          href={whatsappURL}
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-button"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onMouseEnter={() => setShowWhatsAppTooltip(true)}
          onMouseLeave={() => setShowWhatsAppTooltip(false)}
        >
          <FaWhatsapp className="whatsapp-icon" />
          <span className="pulse-ring"></span>
          <span className="pulse-ring pulse-ring-delay"></span>
        </motion.a>
      </div>
    </>
  );
};

export default ContactUs;
